/** Influencers view component. */

import {
  fetchInfluencersPage,
  influencersWindowCaption,
  isInfluencersWindow,
  type InfluencersWindow,
} from '@/api/services/influencers';
import { showToast } from '@/utils/helpers';
import { isAbortError, logError } from '@/utils/errors';
import { formatLargeNumber, escapeHTML } from '@/utils/formatters';
import { wireArrowKeyTablist } from '@/utils/a11y';
import type { Influencer } from '@/types/responses';

const INFLUENCERS_PAGE_SIZE = 100;

let allInfluencers: Influencer[] = [];
let sortColumn: string | null = null;
let sortDirection: 'asc' | 'desc' = 'desc';
const originalRanks: Map<string, number> = new Map();

let influencersTimeWindow: InfluencersWindow = '24h';
let influencersToolbarWired = false;
let influencersLoadedPage = 1;
let influencersHasNext = false;
let influencersTotal = 0;
let influencersLoadMoreBusy = false;
let influencersLoading = false;

function syncInfluencersCaption(window: InfluencersWindow): void {
  const period = document.getElementById('influencers-window-period');
  if (!period) {
    return;
  }
  period.textContent = influencersWindowCaption(window);
}

function syncInfluencersTabs(active: InfluencersWindow): void {
  document.querySelectorAll<HTMLButtonElement>('.influencers-window-tab').forEach((btn) => {
    const raw = btn.dataset.influencersWindow;
    const w = raw !== undefined && isInfluencersWindow(raw) ? raw : undefined;
    const on = w === active;
    btn.setAttribute('aria-selected', on ? 'true' : 'false');
    btn.classList.toggle('border-neon-green', on);
    btn.classList.toggle('ring-1', on);
    btn.classList.toggle('ring-neon-green/40', on);
    btn.classList.toggle('text-neon-green', on);
    btn.classList.toggle('text-gray-200', !on);
  });
}

function setInfluencersTabsBusy(busy: boolean): void {
  document.querySelectorAll<HTMLButtonElement>('.influencers-window-tab').forEach((btn) => {
    btn.disabled = busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    btn.classList.toggle('opacity-60', busy);
    btn.classList.toggle('cursor-not-allowed', busy);
  });
}

function renderInfluencersLoadingState(window: InfluencersWindow): void {
  const container = document.getElementById('influencers-table-container');
  if (!container) {
    return;
  }
  container.innerHTML = `
    <div class="py-5 space-y-3">
      <p class="text-secondary text-sm">Loading top influencers for ${escapeHTML(influencersWindowCaption(window))}...</p>
      <div class="h-8 rounded bg-gray-700 animate-pulse"></div>
      <div class="h-8 rounded bg-gray-700 animate-pulse"></div>
      <div class="h-8 rounded bg-gray-700 animate-pulse"></div>
      <div class="h-8 rounded bg-gray-700 animate-pulse"></div>
    </div>
  `;
}

function setInfluencersRefreshBusy(busy: boolean): void {
  const container = document.getElementById('influencers-table-container');
  if (!container) {
    return;
  }
  container.setAttribute('aria-busy', busy ? 'true' : 'false');
  container.classList.toggle('opacity-95', busy);
}

function renderInfluencersErrorState(): void {
  const container = document.getElementById('influencers-table-container');
  if (!container) {
    return;
  }
  container.innerHTML =
    '<p class="text-red-400">Failed to load influencers for this period. Please retry.</p>';
}

function ensureInfluencersToolbar(): void {
  const toolbar = document.getElementById('influencers-window-toolbar');
  if (!toolbar || influencersToolbarWired) {
    return;
  }
  influencersToolbarWired = true;
  toolbar.innerHTML = `
    <span class="text-sm text-gray-400 mr-1 self-center">Period</span>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="24h" aria-selected="true">24h</button>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="7d" aria-selected="false">7d</button>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="30d" aria-selected="false">30d</button>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="3m" aria-selected="false">3 mo</button>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="6m" aria-selected="false">6 mo</button>
    <button type="button" role="tab" class="influencers-window-tab px-2.5 py-1.5 rounded-md text-xs sm:text-sm font-medium bg-gray-800 border border-gray-600 hover:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/50" data-influencers-window="1y" aria-selected="false">1 yr</button>
  `;
  toolbar.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLButtonElement>('[data-influencers-window]');
    const raw = btn?.dataset.influencersWindow;
    if (!raw || !isInfluencersWindow(raw)) {
      return;
    }
    if (raw === influencersTimeWindow) {
      return;
    }
    void loadInfluencersForWindow(raw);
  });
  wireArrowKeyTablist(toolbar, '.influencers-window-tab', (btn) => {
    const raw = btn.dataset.influencersWindow;
    if (!raw || !isInfluencersWindow(raw) || raw === influencersTimeWindow) {
      return;
    }
    void loadInfluencersForWindow(raw);
  });
}

function rebuildMentionRanks(): void {
  const rankedByImpact = [...allInfluencers].sort((a, b) => {
    const ai = Number(a.impact_score ?? 0);
    const bi = Number(b.impact_score ?? 0);
    if (bi !== ai) return bi - ai;
    const mentionsDiff = (b.Mentions || 0) - (a.Mentions || 0);
    if (mentionsDiff !== 0) return mentionsDiff;
    return (a.author_name || '').localeCompare(b.author_name || '');
  });
  originalRanks.clear();
  rankedByImpact.forEach((influencer, index) => {
    const authorName = influencer.author_name || '[deleted]';
    originalRanks.set(authorName, index + 1);
  });
}

function applyInfluencersPayload(influencers: Influencer[], replace: boolean): void {
  if (replace) {
    allInfluencers = influencers;
  } else {
    const seen = new Set(allInfluencers.map((i) => i.author_name));
    for (const row of influencers) {
      if (!seen.has(row.author_name)) {
        allInfluencers.push(row);
        seen.add(row.author_name);
      }
    }
  }
  rebuildMentionRanks();
  sortColumn = 'impact';
  sortDirection = 'desc';
  const sorted = sortInfluencers([...allInfluencers], sortColumn, sortDirection);
  renderInfluencersTable(sorted);
}

async function loadInfluencersForWindow(
  w: InfluencersWindow,
  options?: { softRefresh?: boolean }
): Promise<void> {
  const softRefresh = Boolean(options?.softRefresh);
  const hadExistingRows = allInfluencers.length > 0;
  influencersTimeWindow = w;
  syncInfluencersCaption(w);
  syncInfluencersTabs(w);
  influencersLoadedPage = 1;
  influencersLoading = true;
  setInfluencersTabsBusy(true);
  if (softRefresh && hadExistingRows) {
    setInfluencersRefreshBusy(true);
  } else {
    allInfluencers = [];
    influencersHasNext = false;
    influencersTotal = 0;
    renderInfluencersLoadingState(w);
  }
  try {
    const data = await fetchInfluencersPage({
      window: influencersTimeWindow,
      page: 1,
      pageSize: INFLUENCERS_PAGE_SIZE,
    });
    if (!data || !data.influencers) {
      if (!softRefresh || !hadExistingRows) {
        renderInfluencersErrorState();
      }
      showToast('Failed to fetch influencers data', true, { dedupeKey: 'influencers-fetch-fail' });
      return;
    }
    const p = data.pagination;
    if (p) {
      influencersHasNext = p.has_next;
      influencersTotal = p.total;
      influencersLoadedPage = p.page;
    } else {
      influencersHasNext = false;
      influencersTotal = data.influencers.length;
    }
    applyInfluencersPayload(data.influencers, true);
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }
    logError(error, 'Influencers');
    if (!softRefresh || !hadExistingRows) {
      renderInfluencersErrorState();
    }
    showToast('Failed to load influencers', true, { dedupeKey: 'influencers-load-fail' });
  } finally {
    setInfluencersRefreshBusy(false);
    influencersLoading = false;
    setInfluencersTabsBusy(false);
  }
}

async function appendInfluencersPage(): Promise<void> {
  if (!influencersHasNext || influencersLoadMoreBusy || influencersLoading) {
    return;
  }
  influencersLoadMoreBusy = true;
  renderInfluencersTable(sortInfluencers([...allInfluencers], sortColumn || 'impact', sortDirection));
  try {
    const nextPage = influencersLoadedPage + 1;
    const data = await fetchInfluencersPage({
      window: influencersTimeWindow,
      page: nextPage,
      pageSize: INFLUENCERS_PAGE_SIZE,
    });
    if (!data?.influencers?.length) {
      influencersHasNext = false;
      renderInfluencersTable(sortInfluencers([...allInfluencers], sortColumn || 'impact', sortDirection));
      return;
    }
    influencersLoadedPage = nextPage;
    if (data.pagination) {
      influencersHasNext = data.pagination.has_next;
      influencersTotal = data.pagination.total;
    }
    applyInfluencersPayload(data.influencers, false);
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }
    logError(error, 'InfluencersLoadMore');
    showToast('Failed to load more influencers', true, { dedupeKey: 'influencers-load-more-fail' });
  } finally {
    influencersLoadMoreBusy = false;
  }
}

export async function renderInfluencers(): Promise<void> {
  try {
    document.querySelectorAll('.view').forEach((v) => {
      v.classList.add('hidden');
      (v as HTMLElement).style.display = 'none';
    });

    const view = document.getElementById('view-top_influencers');
    if (view) {
      view.classList.remove('hidden');
      view.style.display = '';
    }

    import('@/stores/navigation').then((module) => {
      module.useNavigationStore.getState().setCurrentView('top_influencers');
    });

    document.querySelectorAll('.nav-link, .nav-link-mobile').forEach((link) => {
      link.classList.remove('active');
      if (link.getAttribute('data-view') === 'top_influencers') {
        link.classList.add('active');
      }
    });

    ensureInfluencersToolbar();
    await loadInfluencersForWindow(influencersTimeWindow);
  } catch (error) {
    logError(error, 'Influencers');
    showToast('Failed to load influencers', true);
  }
}

export async function refreshInfluencersView(): Promise<void> {
  await loadInfluencersForWindow(influencersTimeWindow, { softRefresh: true });
}

function handleSort(column: string): void {
  if (sortColumn === column) {
    sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn = column;
    sortDirection = 'desc';
  }

  const sorted = sortInfluencers([...allInfluencers], sortColumn, sortDirection);
  renderInfluencersTable(sorted);
}

function sortInfluencers(influencers: Influencer[], column: string, direction: 'asc' | 'desc'): Influencer[] {
  return influencers.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (column) {
      case 'author':
        aValue = (a.author_name || '[deleted]').toLowerCase();
        bValue = (b.author_name || '[deleted]').toLowerCase();
        break;
      case 'impact':
        aValue = Number(a.impact_score ?? 0);
        bValue = Number(b.impact_score ?? 0);
        break;
      case 'mentions':
        aValue = a.Mentions || 0;
        bValue = b.Mentions || 0;
        break;
      case 'avg_karma':
        aValue = a.avg_karma || 0;
        bValue = b.avg_karma || 0;
        break;
      case 'positive':
        aValue = a.positive_mentions || 0;
        bValue = b.positive_mentions || 0;
        break;
      case 'negative':
        aValue = a.negative_mentions || 0;
        bValue = b.negative_mentions || 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

function renderInfluencersTable(influencers: Influencer[]): void {
  const container = document.getElementById('influencers-table-container');
  if (!container) return;

  if (influencers.length === 0) {
    container.innerHTML = '<p class="text-secondary">No influencers found.</p>';
    return;
  }

  const getSortIndicator = (column: string): string => {
    const effectiveColumn = column === 'rank' ? 'impact' : column;
    if (sortColumn === effectiveColumn) {
      return sortDirection === 'asc'
        ? ' <span class="text-neon-cyan">↑</span>'
        : ' <span class="text-neon-cyan">↓</span>';
    }
    return ' <span class="text-secondary opacity-50">↕</span>';
  };

  const footer = `
    <div class="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p class="text-sm text-secondary">
        Showing <span class="text-neon-cyan font-medium">${allInfluencers.length}</span>
        ${influencersTotal > 0 ? ` of <span class="text-neon-cyan font-medium">${influencersTotal}</span> ranked authors` : ''}
      </p>
      ${
        influencersHasNext
          ? `<button type="button" id="influencers-load-more-btn" ${influencersLoadMoreBusy ? 'disabled aria-busy="true"' : ''} class="px-4 py-2 rounded-md text-sm font-medium bg-gray-800 border border-neon-cyan/60 text-neon-cyan hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-neon-cyan/40 ${influencersLoadMoreBusy ? 'opacity-60 cursor-not-allowed' : ''}">${influencersLoadMoreBusy ? 'Loading…' : 'Load more'}</button>`
          : influencersTotal > 0 && allInfluencers.length >= influencersTotal
            ? '<span class="text-sm text-gray-500">All ranked authors loaded.</span>'
            : ''
      }
    </div>
  `;

  const table = `
    <table class="w-full">
      <thead>
        <tr class="border-b border-primary">
          <th class="text-left p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="rank">
            Rank${getSortIndicator('rank')}
          </th>
          <th class="text-left p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="author">
            Author${getSortIndicator('author')}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="impact">
            Impact${getSortIndicator('impact')}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="mentions">
            Mentions${getSortIndicator('mentions')}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="avg_karma">
            Avg Karma${getSortIndicator('avg_karma')}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="positive">
            Positive${getSortIndicator('positive')}
          </th>
          <th class="text-right p-2 cursor-pointer hover:bg-gray-700 select-none" data-sort="negative">
            Negative${getSortIndicator('negative')}
          </th>
        </tr>
      </thead>
      <tbody>
        ${influencers
          .map((influencer) => {
            const authorName = influencer.author_name || '[deleted]';
            const rank = originalRanks.get(authorName) || 0;
            return `
          <tr class="border-b border-primary hover:bg-gray-700">
            <td class="text-left p-2">${rank}</td>
            <td class="text-left p-2">
              <span class="text-neon-cyan hover:underline cursor-pointer font-bold" data-author="${escapeHTML(authorName)}">
                ${escapeHTML(authorName)}
              </span>
            </td>
            <td class="text-right p-2 font-medium">${(influencer.impact_score ?? 0).toFixed(1)}</td>
            <td class="text-right p-2">${influencer.Mentions || 0}</td>
            <td class="text-right p-2">${formatLargeNumber(influencer.avg_karma || 0)}</td>
            <td class="text-right p-2 text-green-400">${influencer.positive_mentions || 0}</td>
            <td class="text-right p-2 text-red-400">${influencer.negative_mentions || 0}</td>
          </tr>
        `;
          })
          .join('')}
      </tbody>
    </table>
    ${footer}
  `;

  container.innerHTML = table;

  container.querySelectorAll('th[data-sort]').forEach((header) => {
    header.addEventListener('click', () => {
      const column = header.getAttribute('data-sort');
      if (column) {
        if (column === 'rank') {
          sortColumn = 'impact';
          sortDirection = 'desc';
          const sorted = sortInfluencers([...allInfluencers], 'impact', 'desc');
          renderInfluencersTable(sorted);
        } else {
          handleSort(column);
        }
      }
    });
  });

  const loadMoreBtn = container.querySelector('#influencers-load-more-btn');
  if (loadMoreBtn) {
    loadMoreBtn.addEventListener('click', () => {
      void appendInfluencersPage();
    });
  }

  container.querySelectorAll('[data-author]').forEach((element) => {
    element.addEventListener('click', (e) => {
      const authorName = (e.currentTarget as HTMLElement).getAttribute('data-author');
      if (authorName && authorName !== '[deleted]') {
        window.dispatchEvent(
          new CustomEvent('navigate', {
            detail: {
              view: 'influencer_deep_dive',
              author: authorName,
              influencerWindow: influencersTimeWindow,
            },
          })
        );
      }
    });
  });
}
